/* eslint-disable no-param-reassign */
/* eslint-disable no-mixed-operators */
/**
 * Welcome to the map chest data manager that is supposed to only affect the map chest data
 * that is in the save state.
 *
 * This replaces some functionality mapChestMgr was supposed to do.
 * mapChestMgr is still here just so migration code can happen.
 *
 * Other files you might want to look at if its about map chests:
 * World.js
 * WorldMap.js
 * G.WorldMapChestDynamicLayer.js
 * mapChestMgr.js
 *
 * @author Sandra Koo
 */

const oldMaxChestGateLevel = 600; // maxChestGateLevel. Not applicable to this anymore

export default class MapChestData {
  /**
   * The object is created in saveState. This class modifies the object reference.
   *
   * @param {Object} data
   */
  init(data, oldChests) {
    this.dataReference = data;

    const defaultData = {
      chestCap: oldMaxChestGateLevel,
      unOpenedChests: [],
      chestSeed: 0,
      // oldChestData: [], // Not required but it is there when migration is happening
    };

    /**
     * unOpenedChest (and oldChestData) is an array of objects = {
     *  i: number, // i = index,
     *  sr: number, // sr = star requirement
     *  x: number, // x position
     *  y: number, // y position,
     * }
     */

    this.mergeMissing(this.dataReference, defaultData);

    if (G.firstTime && !OMT.envData.isRestoreProgressPayload) { // If the user is playing the game for the first time, this will generate chests before the first gate
      this.generateNewChestsToNewGate(0, 0);
    }
    if (oldChests) { // If there are any old chests, it will convert and store them
      this.migrateData(G.Helpers.levelDataMgr.getTilemapHeight());
    } else if (this.dataReference.oldChestData) { // If theres no more old chests, it will check them
      this.checkMigratedData(G.Helpers.levelDataMgr.getTilemapHeight());
    }
  }

  /**
  * merge returning users chest data
  * @param {Object} rtr
  * @param {Object} incoming
  */
  mergeMissing(rtr, incoming) {
    for (const key in incoming) {
      if (Object.prototype.hasOwnProperty.call(incoming, key) && !Object.prototype.hasOwnProperty.call(rtr, key)) rtr[key] = incoming[key];
    }
    return rtr;
  }

  /**
   * This sets the map chest cap. Used for old stuff.
   * Will be removed when migration is complete.
   * mcc = map chest cap
   * @param {number} mcc
   */
  setMapChestCap(mcc) {
    if (this.dataReference.chestCap !== mcc) {
      let mapChestCap = mcc;
      if (mapChestCap === undefined) {
        let curLevel = this.getLastPassedLevelNr();
        if (curLevel > oldMaxChestGateLevel) { // Rounds to the next gate
          const gateMgr = G.Helpers.gatesMgr;
          curLevel = gateMgr.getFirstClosedGateLvLIndex();
        }
        mapChestCap = Math.max(oldMaxChestGateLevel, curLevel);
      }
      this.dataReference.chestCap = mapChestCap;
    }
  }

  /**
   * Migrates all visible chests up to the next gate.
   * @param {number} tilemapHeight
   */
  migrateData(tilemapHeight) {
    const mapChestMgr = G.Helpers.mapChestsMgr;
    const nextGatePosition = this.getNextGatePosition(); // Finds the next gate
    const allChests = mapChestMgr.getAllChestsIds(); // Gets every chest
    const keepChests = [];
    const fogDistance = -100; // Grabs chests past the fog (because they're slightly visible)
    for (let i = 0; i < allChests.length; i++) {
      const chestIndex = allChests[i];
      const chest = mapChestMgr.getChestMapPosition(chestIndex); // Get chest position
      const chestOpened = mapChestMgr.isChestOpened(chestIndex); // Is it opened?
      if (chestOpened !== undefined && !chestOpened && chest.y > (nextGatePosition.y + fogDistance)) { // Is it before the gate?
        keepChests.push(chestIndex); // Keep it
      }
    }

    const starReqAtChangePoint = this.calculateStarsAtFormulaChangePoint();
    const convertedChestData = [];

    for (let j = 0; j < keepChests.length; j++) { // Format the data
      // Calculate approximate level of old chest based on star rating
      const starReq = mapChestMgr.getChestStarsRequirement(keepChests[j]);
      const approximatedLevel = this.approximateLevelFromStars(starReq, starReqAtChangePoint);

      // Try to reposition chest as in new chest system. If there is no space,
      // fallback onto its original position
      const chosenPosition = this.pickEmptyPosition(convertedChestData, approximatedLevel, tilemapHeight);
      let migratedChest;

      if (chosenPosition) {
        migratedChest = {
          i: approximatedLevel,
          x: chosenPosition.x,
          y: chosenPosition.y,
          sr: starReq,
        };
      } else {
        const chestFallbackPosition = mapChestMgr.getChestMapPosition(keepChests[j]);
        migratedChest = {
          i: approximatedLevel,
          x: chestFallbackPosition.x,
          y: chestFallbackPosition.y,
          sr: starReq,
        };
      }

      convertedChestData.push(migratedChest);
    }

    if (convertedChestData.length > 0) { // save it
      this.dataReference.unOpenedChests.push(...convertedChestData);
      G.saveState.save();
    }
  }

  /**
   * A function of its own because getFirstClosedGateData can return NULL when there is no gate ahead of you!
   * It is possible to have no gate ahead of you if someone forgets to set it or you are at the last levels
   */
  getNextGatePosition() {
    const gateMgr = G.Helpers.gatesMgr;
    const gate = gateMgr.getFirstClosedGateData(); // Grab gate?
    if (!gate) { // Theres no gate
      const prevGateIndex = gateMgr.getGateDimensionForLevelIndex(G.saveState.getLastPassedLevelNr()); // Get current gate
      const prevGate = gateMgr.getGateObject(prevGateIndex); // current gate (which is technically the one you just passed)
      const prevPrevGate = gateMgr.getGateObject(prevGateIndex - 1); // previous gate

      if (prevGate && prevPrevGate) { // Gate is found
        const prevPos = prevGate.getMapPosition(); // Get position
        const prevPrevPos = prevPrevGate.getMapPosition(); // Get Position
        const difference = prevPos.y - prevPrevPos.y; // Find distance between the two
        const calculatedPos = prevPos.y + difference; // Mock it up
        return {
          x: 0,
          y: calculatedPos,
        };
      }
      return { // x_x
        x: 0,
        y: 0,
      };
    }
    const nextGate = gateMgr.getGateObject(gate.id); // If there is a gate, just get the position
    return nextGate.getMapPosition();
  }

  /**
   * Grabs a copy of the unOpened chests array.
   * If theres old chest data, it merges it together.
   * deepClone gives back a deepClone version so it can be modified without modifying the saveState's
   * @param {boolean} deepClone
   */
  getChestArray(deepClone = false) {
    let consideringArray = this.dataReference.unOpenedChests.slice();
    if (this.dataReference.oldChestData) {
      consideringArray = this.dataReference.oldChestData.concat(consideringArray);
      consideringArray.forEach((chestData, index) => {
        if (this.dataReference.oldChestData.indexOf(chestData) >= 0) {
          // make a copy of the chestData and flag as a old chest
          chestData = Object.assign(chestData, { isOldChest: true });
          // transform old chest data and adjust x,y for new scale
          if (G.OMTsettings.elements.worldMap && G.OMTsettings.elements.worldMap.mapScale != null) {
            const { mapScale } = G.OMTsettings.elements.worldMap;
            chestData.x *= mapScale;
            chestData.y *= mapScale;
          }
          // insert modified chest data back in to the array
          consideringArray[index] = chestData;
        }
      });
    }
    if (deepClone) {
      consideringArray = this.deepClone(consideringArray);
    }
    return consideringArray;
  }

  /**
   * Deep
   * @param {Array} inArr
   */
  deepClone(inArr) {
    const temp = [];
    for (let j = 0; j < inArr.length; j++) {
      const entry = inArr[j];
      temp.push({
        i: entry.i,
        sr: entry.sr,
        x: entry.x,
        y: entry.y,
      });
    }
    return temp;
  }

  /**
   * Fixes positions of old migrated chests and moves them to unOpenedChests.
   * @param {number} tilemapHeight
   */
  checkMigratedData(tilemapHeight) {
    if (this.dataReference.oldChestData) {
      if (this.dataReference.oldChestData.length > 0) { // There are entries
        const fixedChests = [];
        const starReqAtChangePoint = this.calculateStarsAtFormulaChangePoint();

        this.dataReference.oldChestData.forEach((chestData) => {
          // Calculate approximate level of old chest based on star rating
          const approximatedLevel = this.approximateLevelFromStars(chestData.sr, starReqAtChangePoint);

          // Try to reposition chest
          const chosenPosition = this.pickEmptyPosition(fixedChests, approximatedLevel, tilemapHeight);
          fixedChests.push({
            x: chosenPosition ? chosenPosition.x : chestData.x,
            y: chosenPosition ? chosenPosition.y : chestData.y,
            i: approximatedLevel,
            sr: chestData.sr,
          });
        });

        this.dataReference.unOpenedChests.push(...fixedChests);
        G.saveState.save();
      }
      delete this.dataReference.oldChestData; // Remove this...
    }
  }

  /**
   * Tries to find the closest unlocked chest
   * @param {x: number, y: number} curPos
   */
  getClosestUnlockedChest(curPos) {
    const limitDistance = 4000; // The farthest it'll go
    const consideringArray = this.getChestArray(); // Gets all chests to be considered
    const currentSR = G.saveState.getAllStars(); // Current stars
    const maxPos = this.getNextGatePosition().y + 250; // Finds next gate and tries not to go over

    let closestChest;
    let closestChestDistance;
    for (let i = 0; i < consideringArray.length; i++) {
      const chest = consideringArray[i];
      if (chest.y < maxPos) { continue; }
      const distance = Math.abs(chest.y - curPos.y);
      if (distance < limitDistance && currentSR >= chest.sr) { // Is not far and can be unlocked
        if (!closestChest || (closestChest && distance < closestChestDistance)) { // If there was a closer chest, this is now the closest
          closestChest = chest;
          closestChestDistance = distance;
        }
      }
    }
    return closestChest;
  }

  /**
   * Opens the chest, marks it as open (by removing it from data lol)
   * and returns the gifts inside
   * @param {number} id
   */
  openChest(id) {
    const chestData = this.getChestArray();
    const chest = chestData.filter((cData) => cData.i === id)[0];
    if (!chest) {
      console.log('Invalid chest was opened');
      return [];
    }

    const giftFormula = G.gift.getMapChestFormulaGifts();
    const prizes = this.getGiftFromChest(this.dataReference.chestSeed, giftFormula); // Gifts!

    this.dataReference.chestSeed++; // Increments seed.
    if (this.dataReference.chestSeed > (giftFormula.length - 1)) { // Keeps it within boundaries
      this.dataReference.chestSeed = 0;
    }

    let originatedArray = this.dataReference.unOpenedChests; // Was this chest from unOpenedChests?
    let chestIndex = originatedArray.indexOf(chest);
    if (chestIndex === -1) { // Nope!
      originatedArray = this.dataReference.oldChestData; // Was this chest from oldChestData?
      chestIndex = originatedArray.indexOf(chest);
      if (chestIndex === -1) { // Uh... Huh?
        // Wh... How did this...
        console.log('Warning: Invalid index for opened chest');
      }
    }
    originatedArray.splice(chestIndex, 1);

    return prizes;
  }

  /**
   * Returns the gifts from the chest based on the chestSeed
   * @param {number} chestSeed
   * @param {Array} giftFormula
   */
  getGiftFromChest(chestSeed, giftFormula) {
    const chosenPrize = giftFormula[chestSeed];

    const coinPrize = Phaser.Math.between(chosenPrize.coin.min, chosenPrize.coin.max); // Coins

    let ingameBooster; // Initialized here first, just in case its (not) needed
    let preLevelBooster;
    const prizes = [['coin', coinPrize]]; // Put in the coins first
    for (let i = 0; i < chosenPrize.booster.length; i++) { // Checks the booster gifts
      const boost = chosenPrize.booster[i];
      switch (boost.type) {
        case G.gift.GiftContentType.RandomInGameBooster: // Is a random in game booster
          if (!ingameBooster || (ingameBooster && ingameBooster.length === 0)) { // Doesn't exist yet or has been exhausted
            ingameBooster = G.gift.getAvailableInGameBoosters();
          }
          { // Stupid switch scoping issue
            const selectedIndex = Phaser.Math.between(0, ingameBooster.length - 1);
            prizes.push([ingameBooster[selectedIndex], boost.amount]); // Prize goes in
            ingameBooster.splice(selectedIndex, 1); // Takes it out to avoid repetition
          }
          break;
        case G.gift.GiftContentType.RandomPreLevelBooster: // Is a random pre-level booster
          if (!preLevelBooster || (preLevelBooster && preLevelBooster.length === 0)) { // Doesn't exist yet or has been exhausted
            preLevelBooster = G.gift.getAvailablePreLevelBoosters();
            const plusThree = preLevelBooster.indexOf(G.gift.GiftContentType.StartBoosterDefaultMoves); // removes +moves boosters
            const plusFive = preLevelBooster.indexOf(G.gift.GiftContentType.StartBoosterSecondaryMoves); // Because +3 or +5 can get messy
            if (plusThree > -1) {
              preLevelBooster.splice(plusThree, 1);
            }
            if (plusFive > -1) {
              preLevelBooster.splice(plusFive, 1);
            }
            if (preLevelBooster.length === 0) { // If by removing these we don't have any boosters
              preLevelBooster = G.gift.getAvailableInGameBoosters(); // Just return them as in game boosters
            }
          }
          { // Stupid switch scoping issue
            const selectedIndex = Phaser.Math.between(0, preLevelBooster.length - 1);
            prizes.push([preLevelBooster[selectedIndex], boost.amount]); // Prize goes in
            preLevelBooster.splice(selectedIndex, 1); // Takes it out to avoid repetition
          }
          break;
        default: break; // Linting...!!!!
      }
    }

    return prizes;
  }

  /**
   * Generates new chests and saves them when a gate is unlocked
   * Initially called from G.WorldMapGateLayer.Gate class (at the bottom) and signal snakes up its way to G.WorldMapChestDynamicLayer.generateNewChests();
   * CAN CAUSE ISSUES IF THIS GETS CALLED MORE THAN ONCE AT A GIVEN GATE.
   * MAKE SURE THE SIGNAL IS WORKING PROPERLY BY SENDING ONE SIGNAL PER GATE!!!
   * @param {number} startGateId
   * @param {number} tilemapHeight
   */
  generateNewChestsToNewGate(startGateId, tilemapHeight) {
    const gateMgr = G.Helpers.gatesMgr;
    const justOpenedGateLevel = startGateId === 0 ? 0 : gateMgr.getGateObject(startGateId).getLevelIndex(); // If theres no gate, must be 0 (new)
    let levelsToNextGate;
    try { // Attempting to get gate object (it can throw)
      const nextGate = gateMgr.getGateObject(startGateId + 1);
      levelsToNextGate = nextGate.getLevelIndex() - justOpenedGateLevel; // Find out how far away the gate is
    } catch (err) {
      // Gate object does not exist (because its at the end)
      const max = G.Helpers.levelDataMgr.getMaxLevelIndex();
      levelsToNextGate = max - justOpenedGateLevel; // Find out how far away the last level is
    }
    const existingIndex = this.dataReference.unOpenedChests.map((unOpened) => unOpened.i); // An array of every chest index, so duplicates cannot be made
    const numOfChest = Math.floor(levelsToNextGate / G.OMTsettings.mapChest.reoccuringLevel); // How many chests are being created in this gate section
    const saveChest = []; // To concat
    for (let i = 0; i < numOfChest; i++) { // Begin adding in chests
      const levelOfChest = justOpenedGateLevel + (i * G.OMTsettings.mapChest.reoccuringLevel); // Which chest is this!?
      if (levelOfChest === 0 || existingIndex.indexOf(levelOfChest) > -1) { continue; } /* eslint-disable-line no-continue */ // If level 0, skip it!

      let starAmount = 0; // How many stars does this need to open?
      if (levelOfChest < G.OMTsettings.mapChest.formulaChangePoint) {
        starAmount = 26 * (levelOfChest / G.OMTsettings.mapChest.reoccuringLevel);
      } else { // At some point the formula changes
        starAmount = (26 * (G.OMTsettings.mapChest.formulaChangePoint / G.OMTsettings.mapChest.reoccuringLevel))
          + (27 * ((levelOfChest - G.OMTsettings.mapChest.formulaChangePoint) / G.OMTsettings.mapChest.reoccuringLevel));
      }

      const chosenPosition = this.pickEmptyPosition(saveChest, levelOfChest + 1, tilemapHeight);

      saveChest.push({ // Save it
        i: levelOfChest,
        sr: starAmount,
        x: chosenPosition.x,
        y: chosenPosition.y,
      });
    }
    const mutableChests = this.deepClone(saveChest); // Deep clone this so WorldMapChestDynamicLayer can do its weird stuff
    this.dataReference.unOpenedChests = this.dataReference.unOpenedChests.concat(saveChest); // Save it in savestate
    return mutableChests;
  }

  /**
   * Finds all possible positions the chest can sit on based on whats in G.OMTSettings
   * @param {number} startLevel
   * @param {number} tilemapHeight
   */
  possiblePositions(startLevel, tilemapHeight) {
    // Tries to approximate where the level is based on how the tile map repeats
    // Because of the saga map modifications this can be different per game, so I added it as a setting
    // defaults to 25 because that was the original value
    const closestStartTilemap = startLevel / (G.OMTsettings.mapChest.mapTileSizeInLevels || 25);
    const floored = Math.floor(closestStartTilemap); // Floor
    const ratio = closestStartTilemap - floored; // Find the percentage ratio on where it is on the tile map
    const maxPos = this.getNextGatePosition().y + 250; // Finds next gate and tries not to go over

    const levelPosition = G.Helpers.levelDataMgr.getLevelByNumber(startLevel + 1); // Map position of the level node

    /* eslint-disable prefer-destructuring */
    let tileMapPositions;
    if (ratio < 0.33) { // Within the first 33% of the tile map
      tileMapPositions = G.OMTsettings.mapChest.appearanceNodes[0]; // These are ordered from lowest to highest
    } else if (ratio < 0.66) { // Within 66%
      tileMapPositions = G.OMTsettings.mapChest.appearanceNodes[1];
    } else { // Everything else
      tileMapPositions = G.OMTsettings.mapChest.appearanceNodes[2];
    }
    /* eslint-enable prefer-destructuring */

    const startTilemapY = -tilemapHeight * floored; // The approximate position of where the tilemap starts
    let pos = tileMapPositions.filter((position) => { // position.y is the pre-determined spot the chest can be in which was found in EditorWorld
      const posY = startTilemapY + position.y; // Calculates the possible chest position based off of current tilemap position
      return (posY < levelPosition.mapY) && (posY > maxPos); // Is the chest position above the current level node position and before the next gate?
    });

    if (pos.length === 0) { // If there isn't any nodes that fit the above question, take the farthest position and hope for the best...
      pos = [tileMapPositions[tileMapPositions.length - 1]];
    }

    return { // returns its starting position and chest positions
      start: startTilemapY,
      positions: pos,
    };
  }

  /**
   * Picks a random empty position to place a chest
   * @param {Array} savedChests list of chests already placed just now
   * @param {number} level
   * @param {number} tilemapHeight
   * @returns {Object} a free location for a chest
   */
  pickEmptyPosition(savedChests, level, tilemapHeight) {
    const positioning = this.possiblePositions(level, tilemapHeight); // Find all possible positions for this chest
    const pool = positioning.positions; // Pool of positions

    if (pool.length > 1) { // Hopefully there is a lot
      while (pool.length > 0) { // Keep searching for empty spots
        const positionRng = Phaser.Math.between(0, pool.length - 1); // Find a random one
        const target = pool[positionRng];

        // Is a chest here already!?
        const matchingUnopenedChests = this.getChestArray().filter((unopened) => unopened.x === target.x && unopened.y === (positioning.start + target.y));
        const matchingSavedChests = savedChests.filter((chosens) => chosens.x === target.x && chosens.y === (positioning.start + target.y));

        if (matchingUnopenedChests.length === 0 && matchingSavedChests.length === 0) {
          return {
            x: target.x,
            y: positioning.start + target.y,
          };
        }

        // Remove invalid location from pool
        pool.splice(positionRng, 1);
      }

      return null;
    }

    // No randomizing. Be sad!
    return {
      x: pool[0].x,
      y: positioning.start + pool[0].y,
    };
  }

  /**
   * Calculate the star requirement at formula change point
   * @returns {Object} star requirement object for approximateLevelFromStars
   */
  calculateStarsAtFormulaChangePoint() {
    const starsAtChangePoint = 26 * (G.OMTsettings.mapChest.formulaChangePoint / G.OMTsettings.mapChest.reoccuringLevel);
    return {
      starsAtChangePoint,
      starsAfterChangePoint: starsAtChangePoint + (27 * (1 / G.OMTsettings.mapChest.reoccuringLevel)),
    };
  }

  /**
   * Calculate approximate level of old chest based on star rating
   * @param {Object} starReq the star requirement of the chest
   * @param {Object} starReqAtChangePoint star requirement object returned from calculateStarsAtFormulaChangePoint
   * @returns {number} the approximated level
   */
  approximateLevelFromStars(starReq, starReqAtChangePoint) {
    const { starsAtChangePoint, starsAfterChangePoint } = starReqAtChangePoint;

    if (starReq < starsAfterChangePoint) {
      return Math.floor(starReq / 26 * G.OMTsettings.mapChest.reoccuringLevel);
    }

    const remainingStars = starReq - starsAtChangePoint;
    const levelsAfterChangePoint = remainingStars / 27 * G.OMTsettings.mapChest.reoccuringLevel;
    return Math.floor(levelsAfterChangePoint + G.OMTsettings.mapChest.formulaChangePoint);
  }
}
